/* /answers/:id — the answer review workspace.
   No mockup exists for this screen; the visual reference is `docs/design/visit-detail.html` (the
   same archetype) and the normative rules are `docs/design/spec/`.

   Shape (spec/03 archetype C): a queue plus one document. Page head with the position in the
   filtered queue → the photo beside the two things it is judged against (what was asked, what came
   back) → one verdict bar. It replaced a full-screen overlay that had no URL of its own: the old
   screen faked history with `pushState`/`popstate`, so an answer could not be linked, shared or
   reloaded, and its `keydown` listener was never removed — remounting stacked handlers.

   The queue is the LIST's queue. The id of the neighbour cannot be derived here (the order is
   whatever the list was filtered and sorted by), so the filters ride in the query string and one
   `page_size=1` request at the target position resolves it through `List.js`'s own `buildQuery` —
   one definition of the query shape, not two. Same mechanism as `/visit/:id`. */

import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { useHistory, useLocation, useParams } from "react-router-dom";
import { Button, Descriptions, Empty, Flex, Image, Skeleton, Space, Tag, Typography } from "antd";
import { CheckCircleOutlined, ExportOutlined, LeftOutlined, RightOutlined } from "@ant-design/icons";

import Alert from "/src/util/alert";

import RejectTaskModal from "../../component/RejectTaskModal";
import { PersonCell, dash, fullName } from "../../component/table-cells";
import { ANSWER_PAGE, PHOTO_REPORT, SIDE_NAV, VISIT_PAGE } from "../../config/lang";
import { call, getAbortable } from "../../util/action";
import { fill, translate } from "../../util/util";
import { localeFor } from "../merchandiser/route/dateUtils";
import { StatusTag, postApproval, statusOf } from "../visit/parts";
import { localityOf, photosOf, pointOf } from "../../util/answer-fields";
import { buildQuery, searchToState } from "./List";

import "./answers.scss";

const Page = () => {
  const { id } = useParams();
  const history = useHistory();
  const location = useLocation();
  const me = useSelector(state => state.me);
  const lang = me?.language || "en";

  const t = key => translate(ANSWER_PAGE, key, lang);
  const tv = key => translate(VISIT_PAGE, key, lang);
  const tp = key => translate(PHOTO_REPORT, key, lang);

  const [answer, setAnswer] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [rejecting, setRejecting] = useState(false);
  /* Which photo is on the stage. Clamped at render rather than reset on load, so a shorter next
     answer cannot leave the index pointing past its last photo. */
  const [photoIndex, setPhotoIndex] = useState(0);

  /* Position in the filtered queue, handed over by the list so stepping continues it. */
  const params = new URLSearchParams(location.search);
  const queueIndex = Number(params.get("i")) || 0;
  const queueTotal = Number(params.get("n")) || 0;

  /* ------------------------------------------------------------------ load */

  useEffect(() => {
    const controller = new AbortController();
    setLoading(true);
    getAbortable(`answers/${id}/`, controller.signal)
      .then(response => {
        setAnswer(response?.id ? response : null);
        setLoading(false);
      })
      .catch(error => {
        if (error.name === "AbortError") return;
        setAnswer(null);
        setLoading(false);
      });
    return () => controller.abort();
  }, [id]);

  /* ------------------------------------------------------- queue stepping */

  const step = useCallback(
    delta => {
      if (!queueTotal || busy) return;
      const next = queueIndex + delta;
      if (next < 1 || next > queueTotal) return;
      const state = searchToState(location.search, me);
      setBusy(true);
      call({ method: "GET", url: `answers/?${buildQuery({ ...state, page: next, size: 1 })}` }, response => {
        setBusy(false);
        const neighbour = response?.results?.[0];
        if (!neighbour?.id) {
          /* A verdict can shrink the filtered set under us, so the target position may no longer
             exist. Returning silently made the arrow look broken. */
          Alert.info(translate(ANSWER_PAGE, "queue_end", me?.language || "en"), { position: "top-right" });
          return;
        }
        const search = new URLSearchParams(location.search);
        search.set("i", String(next));
        history.push(`/answers/${neighbour.id}?${search.toString()}`);
      });
    },
    [queueIndex, queueTotal, busy, history, location.search, me]
  );

  /* A verdict usually drops the record OUT of the filtered set, so the queue closes up behind us
     and the next record now sits at the CURRENT index — stepping +1 there skips one silently. */
  const stepAfterVerdict = isApproved => {
    const { status } = searchToState(location.search, me);
    const stays = status === "all" || (status === "approved" && isApproved) || (status === "rejected" && !isApproved);
    step(stays ? 1 : 0);
  };

  useEffect(() => {
    const onKey = event => {
      if (event.metaKey || event.ctrlKey || event.altKey) return;
      const tag = (event.target.tagName || "").toLowerCase();
      if (tag === "input" || tag === "textarea" || tag === "select" || event.target.isContentEditable) return;
      /* A modal — the reject flow, or the photo lightbox — owns the keyboard while it is open. */
      if (document.querySelector(".ant-modal-wrap:not([style*='display: none']), .ant-image-preview-wrap")) return;
      if (event.key !== "[" && event.key !== "]") return;
      event.preventDefault();
      step(event.key === "[" ? -1 : 1);
    };
    /* Removed on unmount. The screen this replaced never did, so every remount of the overlay
       added another live handler to `document`. */
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [step]);

  /* --------------------------------------------------------- moderation */

  const decided = (isApproved, comment = "") => {
    setAnswer(prev => (prev ? { ...prev, approval: { is_approved: isApproved, comment } } : prev));
    /* A record's verdict is not the visit's verdict — sharing one message made approving a single
       answer read as approving the whole visit. */
    Alert.success(tv(isApproved ? "record_approved" : "record_rejected"), { position: "top-right" });
  };

  const approve = () => {
    setBusy(true);
    postApproval({ objectId: answer.id, objectType: "answer", isApproved: true, comment: "" }, ok => {
      setBusy(false);
      if (!ok) return;
      decided(true);
      /* Straight on to the next one in the queue: this page exists to be walked. */
      stepAfterVerdict(true);
    });
  };

  /* ------------------------------------------------------------- render */

  const dateFmt = useMemo(
    () => new Intl.DateTimeFormat(localeFor(lang), { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }),
    [lang]
  );
  const dayFmt = useMemo(() => new Intl.DateTimeFormat(localeFor(lang), { day: "2-digit", month: "short", year: "numeric" }), [lang]);

  const backTo = `/answers${location.search ? `?${new URLSearchParams(location.search).toString()}` : ""}`;

  if (loading) {
    return (
      <div className="shell-content an">
        <Skeleton active paragraph={{ rows: 8 }} />
      </div>
    );
  }

  if (!answer) {
    return (
      <div className="shell-content an">
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description={
            <Flex vertical gap={8} align="center">
              <Typography.Text strong>{t("not_found_title")}</Typography.Text>
              <Typography.Text type="secondary">{t("not_found_desc")}</Typography.Text>
              <Button size="small" onClick={() => history.push(backTo)}>
                {t("back_to_list")}
              </Button>
            </Flex>
          }
        />
      </div>
    );
  }

  const status = statusOf(answer.approval);
  const task = answer.task || {};
  const point = pointOf(answer);
  const photos = photosOf(answer);
  /* Clamped, not reset: stepping the queue swaps `answer` without unmounting, and the next one may
     carry fewer photos than the index that was left behind. */
  const shown = Math.min(photoIndex, Math.max(photos.length - 1, 0));
  const supervisors = (point?.responsible_supervisors || []).map(fullName).filter(Boolean);
  const tags = answer.selected_tags || [];

  return (
    <div className="shell-content an">
      {/* NOT sticky: two actions and a short page, so archetype B's measured-offset apparatus is
          not paid for — the same call `/supervisors/:id` made (spec/03#sticky-layering). */}
      <div className="dp-head">
        <div className="dp-head__main">
          <Flex align="center" gap={6}>
            <Typography.Link onClick={() => history.push(backTo)}>{translate(SIDE_NAV, "answers", lang)}</Typography.Link>
            <RightOutlined className="an-crumb-icon" />
            <Typography.Text type="secondary">#{answer.id}</Typography.Text>
          </Flex>
          <div className="dp-head__titlerow">
            <h1 className="dp-title">{[localityOf(point), point?.name].filter(Boolean).join(" · ") || task.title || `#${answer.id}`}</h1>
            <StatusTag status={status} lang={lang} />
          </div>
          <Typography.Text type="secondary">{task.title}</Typography.Text>
        </div>

        {queueTotal > 1 && queueIndex ? (
          <Flex vertical align="flex-end" gap={2}>
            <Flex align="center" gap={2}>
              <Button
                type="text"
                size="small"
                icon={<LeftOutlined />}
                disabled={queueIndex <= 1 || busy}
                aria-label={t("queue_prev")}
                title={`${t("queue_prev")} [`}
                onClick={() => step(-1)}
              />
              <Typography.Text type="secondary" className="an-queue">
                {fill(t("queue_of"), { i: queueIndex, n: queueTotal })}
              </Typography.Text>
              <Button
                type="text"
                size="small"
                icon={<RightOutlined />}
                disabled={queueIndex >= queueTotal || busy}
                aria-label={t("queue_next")}
                title={`${t("queue_next")} ]`}
                onClick={() => step(1)}
              />
            </Flex>
            <Typography.Text type="secondary" className="an-keys">
              {t("keys_hint")}
            </Typography.Text>
          </Flex>
        ) : null}
      </div>

      <div className="an-work">
        {/* The photo pane. `Image.PreviewGroup` replaces the hand-rolled lightbox the old screen
            carried (its own overlay, its own arrows, Font Awesome glyphs and a document-level key
            handler) — antd's preview walks the whole group with the keyboard already.
            ⚠ The strip shows EVERY photo, including the one on the stage. It used to be
            `photos.slice(1)` — "the remaining ones" — which is defensible as a layout and is not
            how anyone reads it: four photos with three thumbnails reads as one photo missing
            (PICSELL-1572). Clicking a thumbnail moves the stage rather than only opening the
            lightbox, so the arrows the ticket asks for are the strip itself. */}
        <div className="dp-card">
          <Flex vertical gap="middle">
            <Typography.Text strong className="dp-sec-title">
              {t("sec_photos")}
            </Typography.Text>
            {photos.length ? (
              <Image.PreviewGroup>
                <div className="an-photo">
                  <Image src={photos[shown]} alt="" />
                  {photos.length > 1 ? (
                    <>
                      <Button
                        className="an-photo__nav an-photo__nav--prev"
                        shape="circle"
                        icon={<LeftOutlined />}
                        aria-label={t("photo_prev")}
                        onClick={() => setPhotoIndex(shown - 1 < 0 ? photos.length - 1 : shown - 1)}
                      />
                      <Button
                        className="an-photo__nav an-photo__nav--next"
                        shape="circle"
                        icon={<RightOutlined />}
                        aria-label={t("photo_next")}
                        onClick={() => setPhotoIndex((shown + 1) % photos.length)}
                      />
                      <span className="an-photo__n lp-num">{fill(t("photo_of"), { i: shown + 1, n: photos.length })}</span>
                    </>
                  ) : null}
                </div>
                {photos.length > 1 ? (
                  <Flex className="an-strip" gap="small">
                    {photos.map((url, index) => (
                      <button
                        key={`${url}-${index}`}
                        type="button"
                        className={`an-strip__t${index === shown ? " an-strip__t--on" : ""}`}
                        aria-label={fill(t("photo_of"), { i: index + 1, n: photos.length })}
                        aria-current={index === shown}
                        onClick={() => setPhotoIndex(index)}>
                        <img src={url} alt="" />
                      </button>
                    ))}
                  </Flex>
                ) : null}
              </Image.PreviewGroup>
            ) : (
              <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={t("no_photos")} />
            )}
          </Flex>
        </div>

        <Flex vertical gap="middle">
          <div className="dp-card">
            {/* The task the answer is against, reachable — a verdict often needs what was actually
                asked for, which is on the task's own page (its template, its images, its history).
                The link carries the queue's query string nowhere: it leaves this queue on purpose. */}
            <Flex className="an-sec-head" align="center" gap="small" wrap>
              <Typography.Text strong className="dp-sec-title">
                {t("sec_task")}
              </Typography.Text>
              {task.id ? (
                <Typography.Link className="an-sec-link" onClick={() => history.push(`/task/${task.id}`)}>
                  <Space size={4}>
                    {t("open_task")}
                    <ExportOutlined />
                  </Space>
                </Typography.Link>
              ) : null}
            </Flex>
            <Descriptions column={1} size="small" colon={false}>
              <Descriptions.Item label={t("k_title")}>
                {task.id ? (
                  <Typography.Link onClick={() => history.push(`/task/${task.id}`)}>{task.title || `#${task.id}`}</Typography.Link>
                ) : (
                  task.title || dash
                )}
              </Descriptions.Item>
              <Descriptions.Item label={t("k_point")}>{[localityOf(point), point?.name].filter(Boolean).join(" · ") || dash}</Descriptions.Item>
              <Descriptions.Item label={t("k_priority")}>{task.priority ? tp(`priority_${task.priority}`) : dash}</Descriptions.Item>
              <Descriptions.Item label={t("k_deadline")}>{task.end ? dayFmt.format(new Date(task.end)) : dash}</Descriptions.Item>
              <Descriptions.Item label={t("k_description")}>{task.description || dash}</Descriptions.Item>
            </Descriptions>
          </div>

          <div className="dp-card">
            <Typography.Text strong className="dp-sec-title">
              {t("sec_response")}
            </Typography.Text>
            <Descriptions column={1} size="small" colon={false}>
              <Descriptions.Item label={t("k_by")}>
                <PersonCell user={answer.visit?.user} strong={false} />
              </Descriptions.Item>
              <Descriptions.Item label={t("k_supervisor")}>{supervisors.join(", ") || dash}</Descriptions.Item>
              <Descriptions.Item label={t("k_when")}>{answer.created ? dateFmt.format(new Date(answer.created)) : dash}</Descriptions.Item>
              {answer.answer ? <Descriptions.Item label={t("k_text")}>{answer.answer}</Descriptions.Item> : null}
              {answer.boolean_answer !== null && answer.boolean_answer !== undefined ? (
                <Descriptions.Item label={t("k_boolean")}>
                  {/* Colour is never the only signal: the word carries the meaning and the tone
                      only reinforces it (spec/01). */}
                  <Tag variant="filled" className={`status-tag status-tag--${answer.boolean_answer ? "ok" : "no"}`}>
                    {t(answer.boolean_answer ? "yes" : "no")}
                  </Tag>
                </Descriptions.Item>
              ) : null}
              {tags.length ? (
                <Descriptions.Item label={t("k_tags")}>
                  <Space size={4} wrap>
                    {tags.map(tag => (
                      <Tag key={tag}>{tag}</Tag>
                    ))}
                  </Space>
                </Descriptions.Item>
              ) : null}
              {answer.visit?.id ? (
                <Descriptions.Item label={t("k_visit")}>
                  <Typography.Link onClick={() => history.push(`/visit/${answer.visit.id}`)}>#{String(answer.visit.id).slice(0, 8)}</Typography.Link>
                </Descriptions.Item>
              ) : null}
              {status === "rejected" && answer.approval?.comment ? (
                <Descriptions.Item label={t("k_verdict_comment")}>{answer.approval.comment}</Descriptions.Item>
              ) : null}
            </Descriptions>
          </div>

          {/* The verdict, while there is one to give. A decided answer keeps its pill in the head
              and offers nothing here — re-deciding is not a thing this endpoint supports. */}
          {status === "pending" ? (
            <Flex className="an-verdict" gap="small" wrap>
              <Button className="an-btn--ok" icon={<CheckCircleOutlined />} loading={busy} onClick={approve}>
                {tv("approv_btn")}
              </Button>
              <Button danger type="primary" disabled={busy} onClick={() => setRejecting(true)}>
                {tv("reject_btn")}
              </Button>
            </Flex>
          ) : null}
        </Flex>
      </div>

      {rejecting && (
        <RejectTaskModal
          target={{ objectId: answer.id, objectType: "answer" }}
          photo={photos[0] || null}
          point={point}
          visitId={answer.visit?.id}
          lang={lang}
          onCancel={() => setRejecting(false)}
          onDone={approval => {
            setRejecting(false);
            /* The reason comes back on the `approvals/` response, so the page can show it without
               a refetch — which matters when there is no queue to step into. */
            decided(false, approval?.comment || "");
            stepAfterVerdict(false);
          }}
        />
      )}
    </div>
  );
};

export default Page;
